
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import TopNavigation from '../../../components/feature/TopNavigation';
import Button from '../../../components/base/Button';

export default function ConfirmBookingPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [isSearching, setIsSearching] = useState(false);
  const [driverFound, setDriverFound] = useState(false);
  
  const bookingData = location.state || {
    pickup: 'الرياض، حي الملز',
    destination: 'الرياض، حي النخيل',
    vehicleType: 'economy',
    price: 15
  };

  const handleConfirmBooking = () => {
    setIsSearching(true);
    
    // Simulate driver search
    setTimeout(() => {
      setDriverFound(true);
      setIsSearching(false);
      
      // Navigate to trip tracking after finding driver
      setTimeout(() => {
        navigate('/trip/tracking', {
          state: {
            ...bookingData,
            driver: {
              name: 'أحمد محمد',
              rating: 4.8,
              phone: '+966501234567',
              carModel: 'تويوتا كامري 2022',
              plateNumber: 'أ ب ج 1234'
            }
          }
        });
      }, 2000);
    }, 3000);
  };

  if (isSearching) {
    return (
      <div className="min-h-screen bg-[#FDFDFD] flex items-center justify-center">
        <div className="text-center px-6">
          <div className="w-24 h-24 bg-[#0056A6]/10 rounded-full flex items-center justify-center mx-auto mb-6">
            <div className="w-12 h-12 border-4 border-[#0056A6] border-t-transparent rounded-full animate-spin"></div>
          </div>
          <h2 className="text-xl font-bold text-gray-800 mb-2">{t('searching')}</h2>
          <p className="text-gray-600">جاري البحث عن أقرب سائق متاح...</p>
        </div>
      </div>
    );
  }

  if (driverFound) {
    return (
      <div className="min-h-screen bg-[#FDFDFD] flex items-center justify-center">
        <div className="text-center px-6">
          <div className="w-24 h-24 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <i className="ri-check-line text-green-600 text-3xl"></i>
          </div>
          <h2 className="text-xl font-bold text-gray-800 mb-2">{t('driverFound')}</h2>
          <p className="text-gray-600">تم العثور على سائق! جاري التوجيه...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDFDFD] pb-20">
      <TopNavigation 
        title="تأكيد الحجز" 
        showBack 
        onBack={() => navigate('/booking')}
      />
      
      <div className="pt-20 px-4">
        {/* Trip Summary */}
        <div className="bg-white rounded-2xl p-6 mb-6 shadow-sm border border-gray-100">
          <h3 className="text-lg font-bold text-gray-800 mb-4">تفاصيل الرحلة</h3>
          
          {/* Route */}
          <div className="space-y-3 mb-6">
            <div className="flex items-center">
              <div className="w-3 h-3 bg-[#0056A6] rounded-full mr-3"></div>
              <div>
                <p className="text-xs text-gray-500">من</p>
                <p className="text-sm font-medium text-gray-800">{bookingData.pickup}</p>
              </div>
            </div>
            
            <div className="border-r border-gray-200 mr-1.5 h-6"></div>
            
            <div className="flex items-center">
              <div className="w-3 h-3 border-2 border-[#F9A826] rounded-full mr-3"></div>
              <div>
                <p className="text-xs text-gray-500">إلى</p>
                <p className="text-sm font-medium text-gray-800">{bookingData.destination}</p>
              </div>
            </div>
          </div>

          {/* Trip Details */}
          <div className="grid grid-cols-2 gap-4 pt-4 border-t border-gray-100">
            <div>
              <p className="text-xs text-gray-500 mb-1">نوع المركبة</p>
              <p className="text-sm font-medium text-gray-800">{t(bookingData.vehicleType)}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-1">المسافة المتوقعة</p>
              <p className="text-sm font-medium text-gray-800">12 كم</p>
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-1">الوقت المتوقع</p>
              <p className="text-sm font-medium text-gray-800">18 دقيقة</p>
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-1">السعر</p>
              <p className="text-lg font-bold text-[#0056A6]">{bookingData.price} ريال</p>
            </div>
          </div>
        </div>

        {/* Payment Method */}
        <div className="bg-white rounded-2xl p-6 mb-6 shadow-sm border border-gray-100">
          <h3 className="text-lg font-bold text-gray-800 mb-4">طريقة الدفع</h3>
          
          <div className="space-y-3">
            {[
              { id: 'cash', name: 'نقداً', icon: 'ri-money-dollar-circle-line', selected: true },
              { id: 'mada', name: 'مدى', icon: 'ri-bank-card-line', selected: false },
              { id: 'visa', name: 'فيزا', icon: 'ri-visa-line', selected: false }
            ].map((method) => (
              <div
                key={method.id}
                className={`flex items-center p-3 rounded-xl border-2 cursor-pointer transition-all duration-200 ${
                  method.selected
                    ? 'border-[#0056A6] bg-[#0056A6]/5'
                    : 'border-gray-100 hover:border-gray-200'
                }`}
              >
                <div className={`w-10 h-10 rounded-full flex items-center justify-center mr-3 ${
                  method.selected ? 'bg-[#0056A6] text-white' : 'bg-gray-100 text-gray-600'
                }`}>
                  <i className={`${method.icon} text-lg`}></i>
                </div>
                <span className="font-medium text-gray-800">{method.name}</span>
                {method.selected && (
                  <i className="ri-check-line text-[#0056A6] text-lg mr-auto"></i>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Confirm Button */}
        <Button
          onClick={handleConfirmBooking}
          className="w-full"
          size="lg"
        >
          <i className="ri-check-line mr-2"></i>
          تأكيد الحجز والبحث عن سائق
        </Button>

        {/* Terms */}
        <p className="text-xs text-gray-500 text-center mt-4 px-4">
          بالمتابعة، أنت توافق على شروط وأحكام الخدمة وسياسة الخصوصية
        </p>
      </div>
    </div>
  );
}
